import "@/App.css";
import { useEffect } from "react";
import {
  createBrowserRouter, Navigate, RouterProvider, useLocation, useParams,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import GlobalHotkeys from "@/components/GlobalHotkeys";
import PwaUpdater from "@/lib/PwaUpdater";
import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppErrorBoundary from "@/components/errors/AppErrorBoundary";
import RouteErrorPage from "@/components/errors/RouteErrorPage";
import NotFoundPage from "@/components/errors/NotFoundPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import AboutPage from "@/pages/AboutPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import CompanyAdminPage from "@/pages/CompanyAdminPage";
import ProjectsPage from "@/pages/ProjectsPage";
import WizardMainShell from "@/pages/WizardMainShell";
import CockpitPage from "@/pages/CockpitPage";
import SectionStudioPage from "@/pages/SectionStudioPage";
import PortfolioPage from "@/pages/PortfolioPage";
import ProjectHealthDashboard from "@/pages/project/ProjectHealthDashboard";
// Iter80 · Feb 2026 — mobile-first Approvals landing page (deep-link target).
import ApprovalsLandingPage from "@/pages/ApprovalsLandingPage";

function ProjectHealthRoute() {
  const { pid } = useParams();
  return <ProjectHealthDashboard projectId={pid} />;
}

// /app/superadmin/:tab wrapper — reads the URL segment and passes it
// as `initialTab` so the Management Console is directly deep-linkable
// (hotfix · Feb 2026).
function SuperAdminTabRoute() {
  const { tab } = useParams();
  return (
    <ProtectedRoute superadmin>
      <SuperAdminConsole initialTab={tab} />
    </ProtectedRoute>
  );
}

// /app/projects/:pid/cpr → deep-link into the CPR "Generate Report" step
// (Iter106 · fix: previous target `/wizard/cpr` was not a valid step id and
//  silently fell back to step 0 / Project Details.)
function CPRRedirect() {
  const { pid } = useParams();
  return <Navigate to={`/app/projects/${pid}/wizard/report_generation`} replace />;
}

// --- Marketing (public) --------------------------------------------------
import MarketingLayout from "@/components/marketing/MarketingLayout";
import LandingPage from "@/pages/marketing/LandingPage";
import ProductPage from "@/pages/marketing/ProductPage";
import HowItWorksPage from "@/pages/marketing/HowItWorksPage";
import WorkflowPage from "@/pages/marketing/WorkflowPage";
import CapabilitiesPage from "@/pages/marketing/CapabilitiesPage";
import ContactPage from "@/pages/marketing/ContactPage";
import AboutUsPage from "@/pages/marketing/AboutUsPage";
import CareersPage from "@/pages/marketing/CareersPage";
import SupportPage from "@/pages/marketing/SupportPage";
import PrivacyPolicyPage from "@/pages/marketing/PrivacyPolicyPage";
import FAQPage from "@/pages/marketing/FAQPage";
import ResourcesPage from "@/pages/marketing/ResourcesPage";
import BlogPage from "@/pages/marketing/BlogPage";
import CaseStudiesPage from "@/pages/marketing/CaseStudiesPage";
import DocumentationPage from "@/pages/marketing/DocumentationPage";
import InboxPage from "@/pages/InboxPage";
import SuperAdminConsole from "@/pages/SuperAdminConsole";
import AdminAnalyticsPage from "@/pages/AdminAnalyticsPage";
import CreateProjectWizard from "@/pages/setup/createproject/CreateProjectWizard";
import ProjectSetupCommandCenter from "@/pages/setup/commandcenter/ProjectSetupCommandCenter";
import ProjectConfigurationPage from "@/pages/setup/commandcenter/ProjectConfigurationPage";
import GISStudio from "@/pages/gis/GISStudio";
import BoreholeLogWorkspace from "@/pages/logs/BoreholeLogWorkspace";
import LogsMapsPage from "@/pages/LogsMapsPage";
import ProjectStageLayout from "@/components/ProjectStageLayout";
import DataBank from "@/pages/logs/DataBank";
import ReportsHubPage from "@/pages/logs/ReportsHubPage";

// Force the browser tab title to a single "SRES" string on every route so the
// tab identity stays consistent across the entire product suite. Individual
// pages can still push more specific titles via `useDocumentTitle` if needed.
function GlobalTabTitle() {
  const { pathname } = useLocation();
  useEffect(() => { document.title = "SRES"; }, [pathname]);
  return null;
}

function ChangePasswordGate({ children }) {
  const { user } = useAuth();
  if (user?.must_change_password && !location.pathname.startsWith("/change-password")) {
    return <Navigate to="/change-password?first=1" replace />;
  }
  return children;
}

const router = createBrowserRouter([
  // ------------------------------------------------------------------
  // Public marketing site — every page wrapped in <MarketingLayout/>
  // ------------------------------------------------------------------
  {
    element: <><GlobalTabTitle /><MarketingLayout /></>,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <LandingPage /> },
      { path: "product", element: <ProductPage /> },
      { path: "how-it-works", element: <HowItWorksPage /> },
      { path: "workflow", element: <WorkflowPage /> },
      { path: "capabilities", element: <CapabilitiesPage /> },
      { path: "contact", element: <ContactPage /> },
      { path: "about-us", element: <AboutUsPage /> },
      { path: "careers", element: <CareersPage /> },
      { path: "support", element: <SupportPage /> },
      { path: "privacy", element: <PrivacyPolicyPage /> },
      { path: "faq", element: <FAQPage /> },
      { path: "pricing", element: <Navigate to="/contact" replace /> },
      { path: "resources", element: <ResourcesPage /> },
      { path: "blog", element: <BlogPage /> },
      { path: "case-studies", element: <CaseStudiesPage /> },
      { path: "docs", element: <DocumentationPage /> },
    ],
  },

  // ------------------------------------------------------------------
  // Auth screens
  // ------------------------------------------------------------------
  { path: "/login", element: <><GlobalTabTitle /><LoginPage /></>, errorElement: <RouteErrorPage /> },
  { path: "/signup", element: <><GlobalTabTitle /><SignupPage /></>, errorElement: <RouteErrorPage /> },
  { path: "/forgot-password", element: <><GlobalTabTitle /><ForgotPasswordPage /></>, errorElement: <RouteErrorPage /> },
  { path: "/about", element: <><GlobalTabTitle /><AboutPage /></>, errorElement: <RouteErrorPage /> },
  {
    path: "/change-password",
    errorElement: <RouteErrorPage />,
    element: (
      <ProtectedRoute><GlobalTabTitle /><ChangePasswordPage /></ProtectedRoute>
    ),
  },

  // ------------------------------------------------------------------
  // Iter80 · Mobile-first Approvals landing page — deep-link target
  // from the CPR PDF footnotes. Renders outside <AppShell> so there is
  // no sidebar chrome; the desktop 4-item sidebar remains untouched.
  // ------------------------------------------------------------------
  {
    path: "/app/approve/:pid/:paragraphId",
    errorElement: <RouteErrorPage />,
    element: (
      <ProtectedRoute>
        <GlobalTabTitle />
        <ApprovalsLandingPage />
      </ProtectedRoute>
    ),
  },

  // ------------------------------------------------------------------
  // Protected workspace — mounted under /app so the marketing site owns /
  // ------------------------------------------------------------------
  {
    path: "/app",
    errorElement: <RouteErrorPage />,
    element: (
      <ProtectedRoute>
        <ChangePasswordGate>
          <GlobalTabTitle />
          <AppShell />
        </ChangePasswordGate>
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <ProjectsPage /> },
      // Dashboard alias — /app/dashboard reads the same view as /app
      { path: "dashboard", element: <ProjectsPage /> },
      // Projects listing alias — /app/projects reads the same view as /app
      { path: "projects", element: <ProjectsPage /> },
      { path: "portfolio", element: <PortfolioPage /> },
      // Sprint P37r · Enterprise Create-Project Wizard — 13-section
      // single-page workflow. Replaces the previous 434-line
      // IntelligentSetupWizard behind the same route.
      { path: "projects/new", element: <CreateProjectWizard /> },
      // Iter82 · Feb 2026 · client mandate — every stage in the 9-step
      // flow now renders inside the same `<ProjectStageLayout />` so
      // the sidebar never changes shape between stages.
      { element: <ProjectStageLayout />,
        children: [
          { path: "projects/:pid/setup",         element: <ProjectSetupCommandCenter /> },
          { path: "projects/:pid/setup/configuration", element: <ProjectConfigurationPage /> },
          { path: "projects/:pid/logs",          element: <BoreholeLogWorkspace /> },
          { path: "projects/:pid/logs-maps",     element: <LogsMapsPage /> },
          { path: "projects/:pid/data-bank",     element: <Navigate to="../reports" replace /> },
          { path: "projects/:pid/reports",       element: <DataBank /> },
          { path: "projects/:pid/gis",           element: <GISStudio /> },
          { path: "projects/:pid/wizard",        element: <Navigate to="project_setup" replace /> },
          { path: "projects/:pid/wizard/:stepId", element: <WizardMainShell /> },
        ],
      },
      // Feb 2026 · Top-level Reports & Downloads hub — lists every
      // project and links into its Reports Panel. Reached from the
      // 4-item sidebar rail.
      { path: "reports", element: <ReportsHubPage /> },
      { path: "projects/:pid/cockpit", element: <CockpitPage /> },
      // Phase 2 · Feb 2026 · Section Studio (2-D cross-section workshop)
      { path: "projects/:pid/sections", element: <SectionStudioPage /> },
      // Sprint P32 · Top-level Project Health Dashboard (setup-propagation trace lives here)
      { path: "projects/:pid/health", element: <ProjectHealthRoute /> },
      // Common aliases users may bookmark or the sidebar may link to
      { path: "projects/:pid/report-readiness", element: <ProjectHealthRoute /> },
      { path: "projects/:pid/cpr", element: <CPRRedirect /> },
      { path: "projects/:id",       element: <Navigate to="wizard/project_setup" replace /> },
      { path: "projects/:id/:tab",  element: <Navigate to="../wizard/project_setup" replace /> },
      {
        // Retired — legacy admin panel is fully covered by /app/superadmin.
        path: "admin",
        element: <Navigate to="/app/superadmin" replace />,
      },
      {
        path: "admin/inbox",
        element: <ProtectedRoute superadmin><InboxPage /></ProtectedRoute>,
      },
      {
        path: "admin/analytics",
        element: <ProtectedRoute superadmin><AdminAnalyticsPage /></ProtectedRoute>,
      },
      {
        path: "superadmin",
        element: <ProtectedRoute superadmin><SuperAdminConsole /></ProtectedRoute>,
      },
      // Deep-linkable Management Console tabs — /app/superadmin/{tab}
      // Valid tab segments: dashboard · pending · users · companies
      // · projects · audit  (roles/permissions/invitations coming
      // with Sprint P37o).
      { path: "superadmin/:tab", element: <SuperAdminTabRoute /> },
      { path: "company-admin", element: <CompanyAdminPage /> },
    ],
  },

  // Legacy redirects — anyone hitting old URLs lands in the workspace root.
  { path: "/portfolio", element: <Navigate to="/app/portfolio" replace /> },
  { path: "/projects/:id", element: <Navigate to="/app" replace /> },
  { path: "/admin", element: <Navigate to="/app/superadmin" replace /> },
  { path: "/company-admin", element: <Navigate to="/app/company-admin" replace /> },

  // ------------------------------------------------------------------
  // Catch-all — branded 404 page for every unknown URL.
  // Must appear LAST so it never shadows a valid route above.
  // ------------------------------------------------------------------
  { path: "*", element: <NotFoundPage />, errorElement: <RouteErrorPage /> },
]);

export default function App() {
  return (
    <div className="App">
      <AppErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <GlobalHotkeys />
            <RouterProvider router={router} />
            <ThemedToaster />
          </AuthProvider>
        </ThemeProvider>
      </AppErrorBoundary>
    </div>
  );
}

function ThemedToaster() {
  return (
    <Toaster
      position="bottom-right"
      theme="light"
      toastOptions={{
        style: {
          background: "#FFFFFF",
          border: "1px solid #E4E7EE",
          color: "#14181F",
          fontFamily: "IBM Plex Sans",
        },
      }}
    />
  );
}
